import { Directive, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { UserState } from '../../shared';
import { DateTime } from 'luxon';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
import * as i2 from "@ngx-translate/core";
export class UserTimezoneDateDirective {
  el;
  renderer;
  viewContainer;
  store;
  translateService;
  destroyed$;
  /**
   * The date to display in the user timezone. The date can be a Date object, a number (milliseconds since UTC epoch), or an ISO string
   */
  date;
  /**
   * Specifies the date format used for displaying dates.
   * This format aligns with Angular's DatePipe conventions.
   * For more details, see: https://angular.dev/api/common/DatePipe?tab=usage-notes
   */
  dateFormat;
  _userTimezone;
  _defaultTimezone;
  skeletonRef;
  constructor(el, renderer, viewContainer, store, translateService, destroyed$) {
    this.el = el;
    this.renderer = renderer;
    this.viewContainer = viewContainer;
    this.store = store;
    this.translateService = translateService;
    this.destroyed$ = destroyed$;
  }
  ngOnInit() {
    this._defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.store.select(UserState.user).pipe(takeUntilDestroyed(this.destroyed$)).subscribe(user => {
      this._userTimezone = user?.timeZone?.zoneId || this._defaultTimezone;
      this.updateDate();
    });
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyed$)).subscribe(() => {
      this.updateDate();
    });
  }
  ngOnChanges(changes) {
    const dateChanges = changes['date'];
    const dateChanged = dateChanges?.firstChange || dateChanges?.currentValue !== dateChanges?.previousValue;
    const dateFormatChanges = changes['dateFormat'];
    const dateFormatChanged = dateFormatChanges?.firstChange || dateFormatChanges?.currentValue !== dateFormatChanges?.previousValue;
    if (dateChanged && !this.date) {
      this.date = '';
    }
    if (dateChanged || dateFormatChanged) {
      this.updateDate();
    }
  }
  updateDate() {
    if (!this._userTimezone || this.date === undefined) {
      this.showLoadingSpinner();
      return;
    }
    this.hideLoadingSpinner();
    const dt = DateTime.fromJSDate(new Date(this.date)).setZone(this._userTimezone);
    const timezoneOffset = dt.toFormat('ZZ');
    const currentLanguage = this.translateService.currentLang;
    const datePipe = new DatePipe(currentLanguage);
    const formattedDate = datePipe.transform(this.date, this.dateFormat, timezoneOffset, currentLanguage);
    this.renderer.setProperty(this.el.nativeElement, 'innerText', formattedDate);
  }
  showLoadingSpinner() {
    if (this.skeletonRef) return;
    this.skeletonRef = this.viewContainer.createComponent(NgxSkeletonLoaderComponent);
    this.skeletonRef.instance.theme = {
      height: '15px',
      width: '40px',
      margin: '0'
    };
    this.skeletonRef.instance.count = 1;
    this.skeletonRef.instance.appearance = 'line';
  }
  hideLoadingSpinner() {
    if (this.skeletonRef) {
      this.viewContainer.clear();
      this.skeletonRef = undefined;
    }
  }
  static ɵfac = function UserTimezoneDateDirective_Factory(t) {
    return new (t || UserTimezoneDateDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i1.Store), i0.ɵɵdirectiveInject(i2.TranslateService), i0.ɵɵdirectiveInject(i0.DestroyRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: UserTimezoneDateDirective,
    selectors: [["", "geauiUserTimezoneDate", ""]],
    inputs: {
      date: "date",
      dateFormat: "dateFormat"
    },
    standalone: true,
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserTimezoneDateDirective, [{
    type: Directive,
    args: [{
      selector: '[geauiUserTimezoneDate]',
      standalone: true
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: i0.ViewContainerRef
  }, {
    type: i1.Store
  }, {
    type: i2.TranslateService
  }, {
    type: i0.DestroyRef
  }], {
    date: [{
      type: Input
    }],
    dateFormat: [{
      type: Input
    }]
  });
})();
